import {
  closestCenter, DndContext, DragEndEvent, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove, horizontalListSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import moment from 'moment-timezone';
import React, {
  ReactNode, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Badge, Button, Form, Modal, Overlay, Popover, Spinner,
} from 'react-bootstrap';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { PageLayout } from '../../../dorian-shared/types/pageLayout/PageLayout';
import { ConfirmModal } from '../../ui/ConfirmModal/ConfirmModal';
import { DragHandle } from '../../ui/DragHandle/DragHandle';
import { PageWrapper } from '../../ui/PageWrapper';
import { showToast } from '../../ui/utils';
import { OfferDateTimeField } from '../Offers/OfferDateTimeField';
import { utcToZonedWallClock, zonedWallClockToUtc } from '../Offers/offerTimeZoneConversion';
import { useTimeZoneOptions } from '../Offers/useTimeZoneOptions';
import { useUnsavedChangesGuard } from '../Offers/useUnsavedChangesGuard';
import { fromDateTimeLocalValue, toDateTimeLocalValue } from '../Offers/offerDateTimeFieldLogic';
import { ToolComponentProps } from '../ToolPage/toolComponentProps';
import { exitToolView } from '../../utils/toolNavigationUtils';
import { BookPicker } from './BookPicker';
import {
  apiErrorMessage, buildSaveDiff, DiscoverShelfBook, CONTINUE_PLAYING_HASHTAG, duplicateBooks,
  fetchDiscoverShelves, isDiffEmpty, newKey, PACIFIC_TIME_ZONE, resolvePreview, saveDiscoverShelves,
  scheduleStatus, validateDatePair, validateHashtag, validateName, WorkingItem, WorkingShelf,
} from './discoverShelvesApi';
import { CoverDialog } from './CoverDialog';
import {
  CARD_OUTER_WIDTH, copyActions, formatSchedule, ItemCard, ItemCardAction,
} from './ItemCard';

const TIME_ZONE_STORAGE_KEY = 'discoverShelvesTimeZone';
// Bootstrap's success light green (also used in the Story Branches hub option cards), so preview mode is obvious.
const PREVIEW_SHELF_BACKGROUND = '#d4edda';
const DEFAULT_START_HOUR = 8;

function readStoredTimeZone() {
  try {
    return localStorage.getItem(TIME_ZONE_STORAGE_KEY) || PACIFIC_TIME_ZONE;
  } catch {
    return PACIFIC_TIME_ZONE;
  }
}

// ------------------------------------------------------------------
// Dates modal (shelves and items): both or neither, shown in the chosen time zone.

interface DatesModalProps {
  title: string;
  startTime: string | null;
  endTime: string | null;
  timeZone: string;
  onSave: (startTime: string | null, endTime: string | null) => void;
  onCancel: () => void;
}

function DatesModal(props: DatesModalProps) {
  const {
    title, startTime, endTime, timeZone, onSave, onCancel,
  } = props;
  const [start, setStart] = useState(startTime ? utcToZonedWallClock(startTime, timeZone) : '');
  const [end, setEnd] = useState(endTime ? utcToZonedWallClock(endTime, timeZone) : '');
  const [scheduled, setScheduled] = useState(Boolean(startTime));

  const enableSchedule = (enabled: boolean) => {
    setScheduled(enabled);
    if (enabled && !start) {
      // New schedules default to 8:00 AM in the display time zone (Pacific unless changed).
      const defaultStart = moment.tz(timeZone).startOf('day').hour(DEFAULT_START_HOUR);
      setStart(defaultStart.format('YYYY-MM-DD HH:mm:ss'));
      setEnd(defaultStart.clone().add(7, 'days').format('YYYY-MM-DD HH:mm:ss'));
    }
  };

  const startUtc = scheduled && start ? zonedWallClockToUtc(start, timeZone) : null;
  const endUtc = scheduled && end ? zonedWallClockToUtc(end, timeZone) : null;
  const error = scheduled ? validateDatePair(startUtc, endUtc) : null;

  return (
    <Modal show onHide={onCancel}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Check
          type="radio"
          id="discover-shelves-always"
          name="discover-shelves-schedule"
          label="Always show"
          checked={!scheduled}
          onChange={() => enableSchedule(false)}
        />
        <Form.Check
          type="radio"
          id="discover-shelves-scheduled"
          name="discover-shelves-schedule"
          label="Scheduled"
          checked={scheduled}
          onChange={() => enableSchedule(true)}
        />
        {scheduled && (
          <div className="mt-2">
            <OfferDateTimeField label={`Start (${timeZone})`} value={start} onChange={setStart} />
            <OfferDateTimeField label={`End (${timeZone}, not included)`} value={end} onChange={setEnd} />
            {error && <Alert variant="danger">{error}</Alert>}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button disabled={Boolean(error)} onClick={() => onSave(startUtc, endUtc)}>OK</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ------------------------------------------------------------------
// Hashtag button: "#" or "#<hashtag>"; clicking opens a popover to set or clear it.

interface HashtagButtonProps {
  shelfKey: string;
  value: string;
  disabled: boolean;
  validate: (hashtag: string) => string | null;
  onSave: (hashtag: string) => void;
}

function HashtagButton(props: HashtagButtonProps) {
  const {
    shelfKey, value, disabled, validate, onSave,
  } = props;
  const target = useRef<HTMLButtonElement>(null);
  const [show, setShow] = useState(false);
  const [draft, setDraft] = useState(value);
  const error = validate(draft);

  const open = () => {
    setDraft(value);
    setShow(true);
  };
  const save = (next: string) => {
    onSave(next.trim());
    setShow(false);
  };

  return (
    <>
      <Button
        ref={target}
        size="sm"
        variant="secondary"
        disabled={disabled}
        title="Hashtag searched when the shelf header is tapped in the app"
        onClick={open}
      >
        #
        {value}
      </Button>
      <Overlay target={target.current} show={show} placement="bottom-start" rootClose onHide={() => setShow(false)}>
        <Popover id={`hashtag-popover-${shelfKey}`}>
          <Popover.Title as="h3">Hashtag</Popover.Title>
          <Popover.Content>
            <Form.Control
              size="sm"
              autoFocus
              value={draft}
              placeholder="e.g. fantasy"
              isInvalid={Boolean(error)}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter' && !error) save(draft);
                if (event.key === 'Escape') setShow(false);
              }}
            />
            <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
            <div className="small text-muted mt-1">Searched when the shelf header is tapped. No spaces or emojis.</div>
            <div className="d-flex justify-content-end mt-2" style={{ gap: 8 }}>
              <Button size="sm" variant="outline-danger" disabled={!value} onClick={() => save('')}>Clear</Button>
              <Button size="sm" variant="secondary" onClick={() => setShow(false)}>Cancel</Button>
              <Button size="sm" disabled={Boolean(error)} onClick={() => save(draft)}>OK</Button>
            </div>
          </Popover.Content>
        </Popover>
      </Overlay>
    </>
  );
}

// ------------------------------------------------------------------
// Sortable wrappers

function SortableItem(props: { id: string; disabled: boolean; children: ReactNode }) {
  const { id, disabled, children } = props;
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id, disabled });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : undefined,
        cursor: disabled ? undefined : 'grab',
        flexShrink: 0,
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

function SortableShelf(props: {
  id: string;
  disabled: boolean;
  children: (handle: ReactNode) => ReactNode;
}) {
  const { id, disabled, children } = props;
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id, disabled });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 3 : undefined,
        boxShadow: isDragging ? '0 10px 20px rgba(0, 0, 0, 0.2)' : undefined,
      }}
    >
      {children(<DragHandle attributes={attributes} listeners={listeners} disabled={disabled} title="Drag to reorder shelves" />)}
    </div>
  );
}

// ------------------------------------------------------------------
// Tool page

type DialogState =
  | { type: 'shelfDates'; shelfKey: string }
  | { type: 'itemDates'; shelfKey: string; itemKey: string }
  | { type: 'cover'; shelfKey: string; itemKey: string }
  | { type: 'addBook'; shelfKey: string }
  | { type: 'copy'; shelfKey: string; itemKey: string }
  | { type: 'deleteShelf'; shelfKey: string }
  | null;

export function DiscoverShelves(props: ToolComponentProps) {
  const { auth, history, toolRole } = props;
  const user = auth.getUser();
  const canEdit = toolRole === 'editor';
  const timeZoneOptions = useTimeZoneOptions();

  const [original, setOriginal] = useState<WorkingShelf[]>([]);
  const [working, setWorking] = useState<WorkingShelf[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [onlyPromotionItems, setOnlyPromotionItems] = useState(false);
  const [timeZone, setTimeZoneState] = useState(readStoredTimeZone);
  const [previewOn, setPreviewOn] = useState(false);
  const [previewAt, setPreviewAt] = useState(() => utcToZonedWallClock(new Date().toISOString(), readStoredTimeZone()));
  const [dialog, setDialog] = useState<DialogState>(null);
  const [copyTargetKey, setCopyTargetKey] = useState('');
  const [renameConfirm, setRenameConfirm] = useState<string[] | null>(null);
  // Clear Books: one shelf at a time is in selection mode, with the chosen item keys.
  const [clearing, setClearing] = useState<{ shelfKey: string; selected: Set<string> } | null>(null);
  // Opened from a promotion's Discover Shelves tab: highlight that promotion's items.
  const [highlightPromotionId] = useState(() => Number(new URLSearchParams(window.location.search).get('promotion')) || null);

  const diff = useMemo(() => buildSaveDiff(original, working), [original, working]);
  const isDirty = !isDiffEmpty(diff);
  const guard = useUnsavedChangesGuard(isDirty);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    fetchDiscoverShelves()
      .then((shelves) => {
        setOriginal(shelves);
        setWorking(shelves);
      })
      .catch((err) => setLoadError(apiErrorMessage(err, 'Failed to load discover shelves')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setTimeZone = (next: string) => {
    setTimeZoneState(next);
    setPreviewAt(utcToZonedWallClock(zonedWallClockToUtc(previewAt, timeZone), next));
    try {
      localStorage.setItem(TIME_ZONE_STORAGE_KEY, next);
    } catch {
      // Only a convenience; the default is used next time.
    }
  };

  const previewAtMs = previewOn && previewAt
    ? new Date(zonedWallClockToUtc(previewAt, timeZone)).getTime()
    : Date.now();
  const resolved = useMemo(() => resolvePreview(working, previewAtMs), [working, previewAtMs]);

  const shelfSensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  // A small distance keeps clicks (e.g. the card menu) from starting a drag.
  const itemSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const updateShelf = (shelfKey: string, update: (shelf: WorkingShelf) => WorkingShelf) => {
    setWorking((current) => current.map((shelf) => (shelf.key === shelfKey ? update(shelf) : shelf)));
  };
  const updateItem = (shelfKey: string, itemKey: string, update: (item: WorkingItem) => WorkingItem) => {
    updateShelf(shelfKey, (shelf) => ({
      ...shelf,
      items: shelf.items.map((item) => (item.key === itemKey ? update(item) : item)),
    }));
  };

  const findShelf = (shelfKey: string) => working.find((shelf) => shelf.key === shelfKey);
  const findItem = (shelfKey: string, itemKey: string) => findShelf(shelfKey)?.items.find((item) => item.key === itemKey);

  const handleShelfDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      setWorking((current) => {
        const from = current.findIndex((shelf) => shelf.key === active.id);
        const to = current.findIndex((shelf) => shelf.key === over.id);
        return arrayMove(current, from, to);
      });
    }
  };

  const handleItemDragEnd = (shelfKey: string) => ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      updateShelf(shelfKey, (shelf) => {
        const from = shelf.items.findIndex((item) => item.key === active.id);
        const to = shelf.items.findIndex((item) => item.key === over.id);
        return { ...shelf, items: arrayMove(shelf.items, from, to) };
      });
    }
  };

  const addShelf = () => {
    setWorking((current) => [{
      key: newKey(),
      name: '',
      hashtag: '',
      start_time: null,
      end_time: null,
      items: [],
    }, ...current]);
  };

  const addBook = (shelfKey: string, book: DiscoverShelfBook) => {
    updateShelf(shelfKey, (shelf) => ({
      ...shelf,
      items: [{
        key: newKey(),
        book_uuid: book.uuid,
        start_time: null,
        end_time: null,
        cover_key: null,
        cover_url: null,
        promotion_id: null,
        promo_identifier: null,
        active: 1,
        book,
        promotion: null,
      }, ...shelf.items],
    }));
    setDialog(null);
  };

  const copyItem = (fromShelfKey: string, itemKey: string, toShelfKey: string) => {
    const item = findItem(fromShelfKey, itemKey);
    if (item) {
      updateShelf(toShelfKey, (shelf) => ({ ...shelf, items: [{ ...item, id: undefined, key: newKey() }, ...shelf.items] }));
    }
    setDialog(null);
  };

  const removeItem = (shelfKey: string, itemKey: string) => {
    updateShelf(shelfKey, (shelf) => ({ ...shelf, items: shelf.items.filter((item) => item.key !== itemKey) }));
  };

  const doSave = () => {
    setRenameConfirm(null);
    setSaving(true);
    saveDiscoverShelves(diff)
      .then(() => {
        showToast({ textMessage: 'Discover shelves saved', variant: 'success' });
        load();
      })
      .catch((err) => showToast({
        textMessage: apiErrorMessage(err, 'Failed to save discover shelves', original),
        variant: 'danger',
        timeout: 10000,
      }))
      .finally(() => setSaving(false));
  };

  const handleSave = () => {
    const invalid = working
      .map((shelf) => ({
        shelf,
        error: validateName(shelf.name, working, shelf.key)
          || validateHashtag(shelf.hashtag, working, shelf.key)
          || validateDatePair(shelf.start_time, shelf.end_time),
      }))
      .find((entry) => entry.error);
    if (invalid) {
      showToast({ textMessage: `Shelf "${invalid.shelf.name.trim() || '(new)'}": ${invalid.error}`, variant: 'danger' });
      return;
    }
    const renamed = working
      .filter((shelf) => shelf.id && original.find((before) => before.id === shelf.id)?.name !== shelf.name)
      .map((shelf) => `${original.find((before) => before.id === shelf.id)?.name} → ${shelf.name.trim()}`);
    if (renamed.length) {
      setRenameConfirm(renamed);
      return;
    }
    doSave();
  };

  const shelfChips = (shelf: WorkingShelf) => {
    const chips: { label: string; variant: string }[] = [];
    const status = scheduleStatus(shelf);
    if (shelf.hashtag !== CONTINUE_PLAYING_HASHTAG && !shelf.items.length) {
      chips.push({ label: 'Empty: not shown in app', variant: 'secondary' });
    }
    if (status === 'expired') chips.push({ label: 'Expired', variant: 'secondary' });
    return chips;
  };

  const itemActions = (shelf: WorkingShelf, item: WorkingItem): ItemCardAction[] => {
    if (!canEdit || previewOn) {
      return [];
    }
    const linked = Boolean(item.promotion_id);
    return [
      {
        label: linked ? 'Dates come from the promotion' : 'Edit dates',
        onClick: () => setDialog({ type: 'itemDates', shelfKey: shelf.key, itemKey: item.key }),
        disabled: linked,
      },
      { label: 'Set custom cover', onClick: () => setDialog({ type: 'cover', shelfKey: shelf.key, itemKey: item.key }) },
      {
        label: 'Remove custom cover',
        onClick: () => updateItem(shelf.key, item.key, (current) => ({ ...current, cover_key: null, cover_url: null })),
        disabled: !item.cover_key,
      },
      ...copyActions(item),
      {
        label: 'Copy to shelf…',
        onClick: () => {
          setCopyTargetKey('');
          setDialog({ type: 'copy', shelfKey: shelf.key, itemKey: item.key });
        },
        disabled: linked,
      },
      { label: 'Remove', onClick: () => removeItem(shelf.key, item.key) },
    ];
  };

  const toggleCleared = (itemKey: string) => setClearing((current) => {
    if (!current) {
      return current;
    }
    const selected = new Set(current.selected);
    if (selected.has(itemKey)) {
      selected.delete(itemKey);
    } else {
      selected.add(itemKey);
    }
    return { ...current, selected };
  });

  const deleteCleared = () => {
    if (clearing) {
      updateShelf(clearing.shelfKey, (shelf) => ({
        ...shelf,
        items: shelf.items.filter((item) => !clearing.selected.has(item.key)),
      }));
    }
    setClearing(null);
  };

  const renderItems = (shelf: WorkingShelf) => {
    const shownKeys = resolved.get(shelf.key) || [];
    const isClearing = clearing?.shelfKey === shelf.key;
    if (previewOn) {
      const shown = shownKeys.map((key) => shelf.items.find((item) => item.key === key) as WorkingItem);
      return shown.map((item) => <ItemCard key={item.key} item={item} timeZone={timeZone} />);
    }
    const duplicates = duplicateBooks(shelf);
    const visible = shelf.items.filter((item) => !onlyPromotionItems || item.promotion_id);
    const dragDisabled = !canEdit || onlyPromotionItems || isClearing;
    return (
      <DndContext
        sensors={itemSensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleItemDragEnd(shelf.key)}
      >
        <SortableContext items={visible.map((item) => item.key)} strategy={horizontalListSortingStrategy}>
          {visible.map((item) => {
            const isDuplicate = duplicates.has(item.book_uuid);
            const liveAtPreview = scheduleStatus(item, previewAtMs) === 'live';
            return (
              <SortableItem key={item.key} id={item.key} disabled={dragDisabled}>
                <ItemCard
                  item={item}
                  timeZone={timeZone}
                  isDuplicate={isDuplicate}
                  hiddenByDuplicate={isDuplicate && liveAtPreview && !shownKeys.includes(item.key)}
                  highlighted={Boolean(highlightPromotionId) && item.promotion_id === highlightPromotionId}
                  actions={itemActions(shelf, item)}
                  selection={isClearing ? {
                    selected: Boolean(clearing?.selected.has(item.key)),
                    onToggle: () => toggleCleared(item.key),
                  } : undefined}
                />
              </SortableItem>
            );
          })}
        </SortableContext>
      </DndContext>
    );
  };

  const renderShelf = (shelf: WorkingShelf, handle: ReactNode) => {
    const isContinuePlaying = shelf.hashtag === CONTINUE_PLAYING_HASHTAG;
    const status = scheduleStatus(shelf);
    const isClearing = clearing?.shelfKey === shelf.key;
    const editable = canEdit && !previewOn && !clearing;
    return (
      <div
        className="border rounded p-2 mb-3"
        style={{ background: previewOn ? PREVIEW_SHELF_BACKGROUND : 'white', opacity: status === 'expired' ? 0.6 : 1 }}
      >
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
          {editable && handle}
          <Form.Control
            size="sm"
            style={{ maxWidth: 280 }}
            value={shelf.name}
            placeholder="Name (required)"
            aria-label="Shelf name"
            disabled={!editable}
            isInvalid={Boolean(validateName(shelf.name, working, shelf.key))}
            onChange={(event) => {
              const { value } = event.target;
              updateShelf(shelf.key, (current) => ({ ...current, name: value }));
            }}
          />
          {/* The continue-playing shelf is identified by its hashtag, so that one can't be changed. */}
          <HashtagButton
            shelfKey={shelf.key}
            value={shelf.hashtag}
            disabled={!editable || isContinuePlaying}
            validate={(hashtag) => validateHashtag(hashtag, working, shelf.key)}
            onSave={(hashtag) => updateShelf(shelf.key, (current) => ({ ...current, hashtag }))}
          />
          {/* Calendar icon alone for an always-shown shelf; the dates beside it once scheduled.
              The continue-playing shelf can't be scheduled. */}
          {!isContinuePlaying && (
            <Button
              size="sm"
              variant="secondary"
              disabled={!editable}
              title="Shelf schedule"
              aria-label="Shelf schedule"
              onClick={() => setDialog({ type: 'shelfDates', shelfKey: shelf.key })}
            >
              <FaRegCalendarAlt />
              {shelf.start_time && shelf.end_time && (
                <span className="ml-1">{formatSchedule(shelf.start_time, shelf.end_time, timeZone)}</span>
              )}
            </Button>
          )}
          {shelfChips(shelf).map((chip) => <Badge key={chip.label} variant={chip.variant}>{chip.label}</Badge>)}
          {isClearing && clearing && (
            <div className="ml-auto d-flex align-items-center" style={{ gap: 8 }}>
              <span className="small">{clearing.selected.size} selected</span>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setClearing({ shelfKey: shelf.key, selected: new Set(shelf.items.map((item) => item.key)) })}
              >
                Select all
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setClearing(null)}>Cancel</Button>
              <Button size="sm" variant="danger" disabled={!clearing.selected.size} onClick={deleteCleared}>
                Confirm delete ({clearing.selected.size})
              </Button>
            </div>
          )}
          {/* The continue-playing shelf can't be cleared or deleted. */}
          {editable && !isContinuePlaying && (
            <div className="ml-auto d-flex" style={{ gap: 8 }}>
              <Button
                size="sm"
                variant="outline-danger"
                disabled={!shelf.items.length}
                onClick={() => setClearing({ shelfKey: shelf.key, selected: new Set() })}
              >
                Clear Books
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => setDialog({ type: 'deleteShelf', shelfKey: shelf.key })}
              >
                Delete shelf
              </Button>
            </div>
          )}
        </div>
        {!isContinuePlaying && (
          <div className="d-flex mt-2 pb-2" style={{ gap: 8, overflowX: 'auto', overflowY: 'hidden' }}>
            {editable && (
              <button
                type="button"
                onClick={() => setDialog({ type: 'addBook', shelfKey: shelf.key })}
                style={{
                  width: CARD_OUTER_WIDTH, minHeight: 150, flexShrink: 0, border: '2px dashed #bbb', borderRadius: 6, background: 'none',
                }}
              >
                + Add book
              </button>
            )}
            {renderItems(shelf)}
          </div>
        )}
        {isContinuePlaying && <div className="small text-muted mt-2">Shows each player&apos;s continue-playing list.</div>}
        {/* Item count and last update, tucked in the bottom-right out of the way of the header controls. */}
        {!previewOn && !isContinuePlaying && (
          <div className="small text-muted text-right">
            {shelf.items.length} item{shelf.items.length === 1 ? '' : 's'}
            {shelf.updated_at && ` · updated ${moment(shelf.updated_at).tz(timeZone).format('YYYY-MM-DD h:mm A')}`}
          </div>
        )}
      </div>
    );
  };

  const hiddenInPreview = previewOn ? working.filter((shelf) => !resolved.has(shelf.key)) : [];
  const visibleShelves = working.filter((shelf) => {
    if (previewOn) {
      return resolved.has(shelf.key);
    }
    if (onlyPromotionItems) {
      return shelf.items.some((item) => item.promotion_id);
    }
    return true;
  });

  const pageLayout: PageLayout = {
    header: { title: 'Discover Shelves' },
    sidebar: {
      nav: [{
        title: 'Back', action: () => exitToolView(history), variant: 'secondary', disabled: false,
      }],
    },
  };

  let body;
  if (loading) {
    body = <div className="text-center py-4"><Spinner animation="border" /></div>;
  } else if (loadError) {
    body = <Alert variant="danger">{loadError}</Alert>;
  } else {
    body = (
      <DndContext
        sensors={shelfSensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleShelfDragEnd}
      >
        {previewOn && hiddenInPreview.length > 0 && (
          <div className="pl-3 mb-3" style={{ color: 'black', fontSize: '1.1rem' }}>
            {hiddenInPreview.length === 1 ? '1 shelf isn\'t' : `${hiddenInPreview.length} shelves aren't`}
            {' shown in the app at this time: '}
            {hiddenInPreview.map((shelf) => shelf.name.trim() || '(new shelf)').join(', ')}
          </div>
        )}
        <SortableContext items={visibleShelves.map((shelf) => shelf.key)} strategy={verticalListSortingStrategy}>
          {visibleShelves.map((shelf) => (
            <SortableShelf key={shelf.key} id={shelf.key} disabled={!canEdit || previewOn || onlyPromotionItems}>
              {(handle) => renderShelf(shelf, handle)}
            </SortableShelf>
          ))}
        </SortableContext>
      </DndContext>
    );
  }

  const dialogShelf = dialog ? findShelf(dialog.shelfKey) : undefined;
  const dialogItem = dialog && 'itemKey' in dialog ? findItem(dialog.shelfKey, dialog.itemKey) : undefined;

  return (
    <PageWrapper user={user} page={pageLayout}>
      {/* Controls on top; only the shelves below them scroll. */}
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="pt-3 pb-2" style={{ flexShrink: 0, borderBottom: '1px solid #dee2e6' }}>
          <div className="d-flex align-items-center flex-wrap mb-2 px-3" style={{ gap: 12 }}>
            {canEdit && <Button size="sm" onClick={addShelf} disabled={previewOn}>Add shelf</Button>}
            <Form.Control
              as="select"
              size="sm"
              style={{ maxWidth: 260 }}
              value={timeZone}
              onChange={(event) => setTimeZone(event.target.value)}
            >
              {timeZoneOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Form.Control>
            {canEdit && (
            <div className="ml-auto">
              {isDirty && <Badge variant="warning" className="mr-2">Unsaved changes</Badge>}
              <Button size="sm" variant="secondary" className="mr-2" disabled={!isDirty || saving} onClick={() => setWorking(original)}>
                Discard
              </Button>
              <Button size="sm" disabled={!isDirty || saving} onClick={handleSave}>
                {saving && <Spinner as="span" animation="border" size="sm" className="mr-1" />}
                Save
              </Button>
            </div>
            )}
          </div>
          {/* Fixed height so showing the preview time input doesn't change the row's height. */}
          <div className="d-flex align-items-center flex-wrap pl-3" style={{ gap: 50, minHeight: 31 }}>
            <Form.Check
              type="checkbox"
              id="discover-shelves-only-promotions"
              label="Only promotion items"
              checked={onlyPromotionItems}
              onChange={(event) => setOnlyPromotionItems(event.target.checked)}
            />
            <div className="d-flex align-items-center">
              <Form.Check
                type="checkbox"
                id="discover-shelves-preview"
                label="Preview at"
                checked={previewOn}
                onChange={(event) => setPreviewOn(event.target.checked)}
              />
              {previewOn && (
              <Form.Control
                type="datetime-local"
                size="sm"
                aria-label="Preview time"
                className="ml-2"
                style={{ width: 220 }}
                value={toDateTimeLocalValue(previewAt)}
                onChange={(event) => setPreviewAt(fromDateTimeLocalValue(event.target.value))}
              />
              )}
            </div>
          </div>
        </div>

        <div className="pt-2" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {body}
        </div>
      </div>

      {dialog?.type === 'shelfDates' && dialogShelf && (
        <DatesModal
          title={`Schedule for "${dialogShelf.name}"`}
          startTime={dialogShelf.start_time}
          endTime={dialogShelf.end_time}
          timeZone={timeZone}
          onCancel={() => setDialog(null)}
          onSave={(startTime, endTime) => {
            updateShelf(dialogShelf.key, (shelf) => ({ ...shelf, start_time: startTime, end_time: endTime }));
            setDialog(null);
          }}
        />
      )}
      {dialog?.type === 'itemDates' && dialogItem && (
        <DatesModal
          title={`Schedule for "${dialogItem.book?.title || dialogItem.book_uuid}"`}
          startTime={dialogItem.start_time}
          endTime={dialogItem.end_time}
          timeZone={timeZone}
          onCancel={() => setDialog(null)}
          onSave={(startTime, endTime) => {
            updateItem(dialog.shelfKey, dialogItem.key, (item) => ({ ...item, start_time: startTime, end_time: endTime }));
            setDialog(null);
          }}
        />
      )}
      {dialog?.type === 'cover' && dialogItem && (
        <CoverDialog
          bookUuid={dialogItem.book_uuid}
          onCancel={() => setDialog(null)}
          onConfirm={(cover) => {
            updateItem(dialog.shelfKey, dialogItem.key, (item) => ({ ...item, cover_key: cover.key, cover_url: cover.url }));
            setDialog(null);
          }}
        />
      )}
      {dialog?.type === 'addBook' && (
        <BookPicker onCancel={() => setDialog(null)} onSelect={(book) => addBook(dialog.shelfKey, book)} />
      )}
      {dialog?.type === 'copy' && dialogItem && (
        <ConfirmModal
          show
          isLoading={saving}
          title="Copy to shelf"
          confirmButtonTitle="Copy"
          isConfirmButtonDisabled={!copyTargetKey}
          onCancel={() => setDialog(null)}
          onConfirm={() => copyItem(dialog.shelfKey, dialogItem.key, copyTargetKey)}
        >
          <Form.Control as="select" value={copyTargetKey} onChange={(event) => setCopyTargetKey(event.target.value)}>
            <option value="">Choose a shelf…</option>
            {working
              .filter((shelf) => shelf.key !== dialog.shelfKey && shelf.hashtag !== CONTINUE_PLAYING_HASHTAG)
              .map((shelf) => <option key={shelf.key} value={shelf.key}>{shelf.name || '(new shelf)'}</option>)}
          </Form.Control>
          <div className="small text-muted mt-2">The copy keeps its dates and cover, and goes at the front of the shelf.</div>
        </ConfirmModal>
      )}
      {dialog?.type === 'deleteShelf' && dialogShelf && (
        <ConfirmModal
          show
          isLoading={saving}
          title="Delete shelf?"
          confirmButtonTitle="Delete"
          onCancel={() => setDialog(null)}
          onConfirm={() => {
            setWorking((current) => current.filter((shelf) => shelf.key !== dialogShelf.key));
            setDialog(null);
          }}
        >
          Delete &quot;{dialogShelf.name}&quot; and all its items, including expired ones? This takes effect when you save.
        </ConfirmModal>
      )}
      {renameConfirm && (
        <ConfirmModal
          show
          isLoading={saving}
          title="Rename shelves?"
          confirmButtonTitle="Save"
          onCancel={() => setRenameConfirm(null)}
          onConfirm={doSave}
        >
          <ul>{renameConfirm.map((line) => <li key={line}>{line}</li>)}</ul>
          The name is used by the app as a translation key. Be sure to have the translation data updated.
        </ConfirmModal>
      )}
      {guard.isConfirmVisible && (
        <ConfirmModal
          show
          isLoading={saving}
          title="Leave without saving?"
          confirmButtonTitle="Leave"
          onCancel={guard.cancelLeave}
          onConfirm={guard.confirmLeave}
        >
          You have unsaved changes to the discover shelves.
        </ConfirmModal>
      )}
    </PageWrapper>
  );
}
